/**
 * Contract Hooks
 */

import type {
	Contract,
	ContractPayment,
	ContractProduct,
	ContractService,
	InternetServiceInContract,
	Template,
	SubscriptionPayment,
	PaymentForecast,
	PaymentRecurringCycle,
	ContractChange,
	BillingAccountBalance,
	ForecastCycle,
	ContractSchedule,
} from "@/types/contract";
import type { Note, Setbox, SimCard } from "@/types/customer";
import type { ContractPackageLog, SuspensionPolicy } from "@/types/contract";
import { Service } from "@/types/product";
import type { ContractPrefix } from "@/types/contract";

export const fetchContractAvailableDiscounts = async (
	contractId: string,
	scopes: (
		| "PRODUCT"
		| "CONTENT"
		| "CONTRACT"
		| "DEVICE"
		| "PACKAGE"
		| "ADDITIONAL_SERVICE"
		| "SERVICE"
		| "INTERNET_SERVICE"
	)[],
) => {
	try {
		const url = new URL(
			`${process.env.REACT_APP_API_URL}/contract/${contractId}/discounts/available`,
		);
		url.searchParams.set("scopes", scopes.join(","));

		const res = await fetch(url.toString());
		const data = await res.json();

		if (data.success) {
			return data.data;
		} else {
			throw new Error(data.message);
		}
	} catch (error) {
		console.error(error);
		throw new Error(error instanceof Error ? error.message : "Алдаа гарлаа");
	}
};

export const cancelContractDiscount = async (
	contractId: string,
	discountId: string,
) => {
	try {
		const url = new URL(
			`${process.env.REACT_APP_API_URL}/contract/${contractId}/discount/${discountId}/cancel`,
		);

		const res = await fetch(url.toString(), {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
			},
		});

		const data = await res.json();

		if (data.success) {
			return data.data;
		} else {
			throw new Error(data.message);
		}
	} catch (error) {
		console.error(error);
		throw new Error(error instanceof Error ? error.message : "Алдаа гарлаа");
	}
};

export const updateContractDiscount = async (
	contractId: string,
	discountId: string,
	data: {
		scope?:
			| "CONTRACT"
			| "PACKAGE"
			| "SERVICE"
			| "INTERNET_SERVICE"
			| "DEVICE"
			| "ADDITIONAL_SERVICE"
			| "PRODUCT";
		reset?: "NEVER" | "PER_CYCLE";
		duration?: "ONCE" | "RECURRING" | "FOREVER";
		activeSince?: string | null;
		activeUntil?: string | null;
		maxCycles?: number | null;
		useLimit?: number | null;
	},
) => {
	try {
		const url = new URL(
			`${process.env.REACT_APP_API_URL}/contract/${contractId}/discount/${discountId}`,
		);

		const res = await fetch(url.toString(), {
			method: "PUT",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify(data),
		});

		const resData = await res.json();

		if (resData.success) {
			return resData.data;
		} else {
			throw new Error(resData.message);
		}
	} catch (error) {
		console.error(error);
		throw new Error(error instanceof Error ? error.message : "Алдаа гарлаа");
	}
};

export const getContractDiscountById = async (
	contractId: string,
	discountId: string,
) => {
	try {
		const url = new URL(
			`${process.env.REACT_APP_API_URL}/contract/${contractId}/discount/${discountId}`,
		);

		const res = await fetch(url.toString(), {
			method: "GET",
			headers: {
				"Content-Type": "application/json",
			},
		});

		const data = await res.json();

		if (data.success) {
			return data.data;
		} else {
			throw new Error(data.message);
		}
	} catch (error) {
		console.error(error);
		throw new Error(error instanceof Error ? error.message : "Алдаа гарлаа");
	}
};

export const addDiscountToContract = async (
	contractId: string,
	discounts: {
		discountId: string;
		scope:
			| "CONTRACT"
			| "PACKAGE"
			| "SERVICE"
			| "INTERNET_SERVICE"
			| "DEVICE"
			| "ADDITIONAL_SERVICE"
			| "PRODUCT";
		reset: "NEVER" | "PER_CYCLE";
		duration: "ONCE" | "RECURRING" | "FOREVER";
		serviceId?: string;
		activeSince?: string | null;
		activeUntil?: string | null;
		maxCycles?: number | null;
		useLimit?: number | null;
		productId?: string | null;
	}[],
) => {
	try {
		const url = new URL(
			`${process.env.REACT_APP_API_URL}/contract/${contractId}/discount`,
		);

		const res = await fetch(url.toString(), {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify({ discounts }),
		});

		const data = await res.json();

		if (data.success) {
			return data.data;
		} else {
			throw new Error(data.message);
		}
	} catch (error) {
		console.error(error);
		throw new Error(error instanceof Error ? error.message : "Алдаа гарлаа");
	}
};

export const fetchContractActiveServices = async (contractId: string) => {
	try {
		const url = new URL(
			`${process.env.REACT_APP_API_URL}/contract/${contractId}/active-services`,
		);

		const res = await fetch(url.toString());
		const data = await res.json();

		if (data.success) {
			return data.data as ContractService[];
		} else {
			throw new Error(data.message);
		}
	} catch (error) {
		console.error(error);
		throw new Error("Алдаа гарлаа");
	}
};

export const fetchContractSchedule = async (scheduleId: string) => {
	try {
		const res = await fetch(
			`${process.env.REACT_APP_API_URL}/contract/schedule/${scheduleId}`,
		);
		const data = await res.json();

		if (data.success) {
			return data.data as ContractSchedule;
		} else {
			throw new Error(data.message);
		}
	} catch (error) {
		console.error(error);
		throw new Error("Алдаа гарлаа");
	}
};

export const fetchBillingAccountBalances = async (
	contractId: string,
	params?: {
		billingAccountId?: string;
		page?: number;
		limit?: number;
		startDate?: string;
		endDate?: string;
	},
) => {
	const url = new URL(
		`${process.env.REACT_APP_API_URL}/contract/${contractId}/billing-account/balances`,
	);
	if (params) {
		Object.entries(params).forEach(([key, value]) => {
			if (value !== undefined) {
				url.searchParams.append(key, String(value));
			}
		});
	}

	const res = await fetch(url.toString(), {
		method: "GET",
		headers: {
			"Content-Type": "application/json",
		},
	});

	const data = await res.json();

	if (data.success) {
		return data.data as {
			currentBalance: number;
			balances: BillingAccountBalance[];
			total: number;
		};
	} else {
		throw new Error(data.message);
	}
};

export const fetchContractChanges = async (
	contractId: string,
	params?: {
		page?: number;
		limit?: number;
	},
) => {
	const url = new URL(
		`${process.env.REACT_APP_API_URL}/contract/${contractId}/changes`,
	);
	if (params) {
		Object.entries(params).forEach(([key, value]) => {
			if (value !== undefined) {
				url.searchParams.append(key, String(value));
			}
		});
	}

	const res = await fetch(url.toString(), {
		method: "GET",
		headers: {
			"Content-Type": "application/json",
		},
	});

	const data = await res.json();

	if (data.success) {
		return data.data as {
			total: number;
			changes: ContractChange[];
		};
	} else {
		throw new Error(data.message);
	}
};

export const fetchPaymentForecast = async (
	contractId: string,
	params?: {
		cycleId?: string;
		from?: string;
		to?: string;
	},
) => {
	try {
		const url = new URL(
			`${process.env.REACT_APP_API_URL}/contract/${contractId}/payment-forecast`,
		);

		if (params) {
			Object.entries(params).forEach(([key, value]) => {
				if (value !== undefined) {
					url.searchParams.append(key, String(value));
				}
			});
		}

		const res = await fetch(url.toString());
		const data = await res.json();

		if (data.success) {
			return data.data as PaymentRecurringCycle[];
		} else {
			throw new Error(data.message);
		}
	} catch (error: any) {
		console.error(error);
		throw new Error(error?.message ?? "Алдаа гарлаа");
	}
};

export const fetchPaymentForecastDetailed = async (
	contractId: string,
	params?: {
		cycleId?: string;
		from?: string;
		to?: string;
	},
) => {
	try {
		const url = new URL(
			`${process.env.REACT_APP_API_URL}/contract/${contractId}/forecast-details`,
		);

		if (params) {
			Object.entries(params).forEach(([key, value]) => {
				if (value !== undefined) {
					url.searchParams.append(key, String(value));
				}
			});
		}

		const res = await fetch(url.toString());
		const data = await res.json();

		if (data.success) {
			return data.data as ForecastCycle[];
		} else {
			throw new Error(data.message);
		}
	} catch (error: any) {
		console.error(error);
		throw new Error(error?.message ?? "Алдаа гарлаа");
	}
};

export const createContractPrefix = async (prefix: ContractPrefix) => {
	try {
		const res = await fetch(
			`${process.env.REACT_APP_API_URL}/contract/prefix`,
			{
				method: "POST",
				headers: {
					"Content-Type": "application/json",
				},
				body: JSON.stringify(prefix),
			},
		);

		const data = await res.json();

		if (data.success) {
			return data.data as ContractPrefix;
		} else {
			throw new Error(data.message);
		}
	} catch (error: any) {
		console.error(error);
		throw new Error(error.message ?? "Алдаа гарлаа");
	}
};

export const fetchContractPrefixes = async () => {
	try {
		const res = await fetch(`${process.env.REACT_APP_API_URL}/contract/prefix`);
		const data = await res.json();

		if (data.success) {
			return data.data as ContractPrefix[];
		} else {
			throw new Error(data.message);
		}
	} catch (error) {
		console.error(error);
		throw new Error("Алдаа гарлаа");
	}
};

export const fetchContractPrefix = async (id: string) => {
	try {
		const res = await fetch(
			`${process.env.REACT_APP_API_URL}/contract/prefix/${id}`,
		);
		const data = await res.json();

		if (data.success) {
			return data.data as ContractPrefix;
		} else {
			throw new Error(data.message);
		}
	} catch (error) {
		console.error(error);
		throw new Error("Алдаа гарлаа");
	}
};

export const updateContractPrefix = async (
	id: string,
	prefix: ContractPrefix,
) => {
	try {
		const res = await fetch(
			`${process.env.REACT_APP_API_URL}/contract/prefix/${id}`,
			{
				method: "PUT",
				headers: {
					"Content-Type": "application/json",
				},
				body: JSON.stringify(prefix),
			},
		);

		const data = await res.json();

		if (data.success) {
			return data.data as ContractPrefix;
		} else {
			throw new Error(data.message);
		}
	} catch (error) {
		console.error(error);
		throw new Error("Алдаа гарлаа");
	}
};

export const deleteContractPrefix = async (id: string) => {
	try {
		const res = await fetch(
			`${process.env.REACT_APP_API_URL}/contract/prefix/${id}`,
			{
				method: "DELETE",
				headers: {
					"Content-Type": "application/json",
				},
			},
		);

		const data = await res.json();

		if (data.success) {
			return data.data as ContractPrefix;
		} else {
			throw new Error(data.message);
		}
	} catch (error) {
		console.error(error);
		throw new Error("Алдаа гарлаа");
	}
};

export const fetchSuspensionPolicies = async () => {
	try {
		const res = await fetch(
			`${process.env.REACT_APP_API_URL}/contract/policy/list`,
		);
		const data = await res.json();

		if (data.success) {
			return data.data as SuspensionPolicy[];
		} else {
			throw new Error(data.message);
		}
	} catch (error) {
		console.error(error);
		throw new Error("Алдаа гарлаа");
	}
};

export const fetchSuspensionPolicy = async (id: string) => {
	try {
		const res = await fetch(
			`${process.env.REACT_APP_API_URL}/contract/policy/${id}`,
		);
		const data = await res.json();

		if (data.success) {
			return data.data as SuspensionPolicy;
		} else {
			throw new Error(data.message);
		}
	} catch (error) {
		console.error(error);
		throw new Error("Алдаа гарлаа");
	}
};

export const fetchContractPayments = async (id: string) => {
	try {
		const res = await fetch(
			`${process.env.REACT_APP_API_URL}/contract/${id}/payments`,
		);
		const data = await res.json();

		if (data.success) {
			return {
				payments: data.data.payments as SubscriptionPayment[],
				total: data.data.total as number,
			};
		} else {
			throw new Error(data.message);
		}
	} catch (error) {
		console.error(error);
		throw new Error("Алдаа гарлаа");
	}
};

export const fetchContractInternetService = async (id: string) => {
	try {
		const res = await fetch(
			`${process.env.REACT_APP_API_URL}/contract/internet-service/${id}`,
		);
		const data = await res.json();

		if (data.success) {
			return data.data as InternetServiceInContract;
		} else {
			throw new Error(data.message);
		}
	} catch (error) {
		console.error(error);
		throw new Error("Алдаа гарлаа");
	}
};

export const fetchContractPaymentPreview = async (
	id: string,
	params?: {
		calculationType?: "day" | "full";
		month?: string;
	},
) => {
	try {
		const url = new URL(
			`${process.env.REACT_APP_API_URL}/contract/${id}/payment-preview`,
		);

		if (params?.calculationType) {
			url.searchParams.set("calculationType", params.calculationType);
		}

		if (params?.month) {
			url.searchParams.set("month", params.month);
		}

		const res = await fetch(url.toString());
		const data = await res.json();

		if (data.success) {
			return data.data;
		} else {
			throw new Error(data.message);
		}
	} catch (error) {
		console.error(error);
		throw new Error("Алдаа гарлаа");
	}
};

export const fetchContractPayment = async (id: string) => {
	try {
		const res = await fetch(
			`${process.env.REACT_APP_API_URL}/contract/${id}/rc`,
		);
		const data = await res.json();

		if (data.success) {
			return data.data;
		} else {
			throw new Error(data.message);
		}
	} catch (error) {
		console.error(error);
		throw new Error("Алдаа гарлаа");
	}
};

export const createContractNote = async (
	contractId: string,
	content: string,
	isPinned: boolean,
) => {
	try {
		const url = new URL(
			`${process.env.REACT_APP_API_URL}/contract/${contractId}/note`,
		);
		const res = await fetch(url.toString(), {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify({ content, isPinned }),
		});
		const data = await res.json();

		if (data.success) {
			return data.data as Note;
		} else {
			throw new Error(data.message);
		}
	} catch (error) {
		console.error(error);
		throw new Error(
			error instanceof Error ? error.message || "Алдаа гарлаа" : "Алдаа гарлаа",
		);
	}
};

export const fetchContractNotes = async (
	id: string,
	params?: {
		page?: number;
		limit?: number;
		dateFrom?: string;
		dateTo?: string;
	},
) => {
	try {
		const url = new URL(
			`${process.env.REACT_APP_API_URL}/contract/${id}/notes`,
		);
		if (params?.page) {
			url.searchParams.set("page", params.page.toString());
		}
		if (params?.limit) {
			url.searchParams.set("limit", params.limit.toString());
		}

		if (params?.dateFrom) {
			url.searchParams.set("dateFrom", params.dateFrom);
		}

		if (params?.dateTo) {
			url.searchParams.set("dateTo", params.dateTo);
		}

		const res = await fetch(url.toString());
		const data = await res.json();

		if (data.success) {
			return {
				notes: data.data as Note[],
				total: data.total as number,
			};
		} else {
			throw new Error(data.message);
		}
	} catch (error) {
		console.error(error);
		throw new Error("Алдаа гарлаа");
	}
};

export const fetchContractService = async (
	serviceId: string,
	contractId: string,
) => {
	try {
		const res = await fetch(
			`${process.env.REACT_APP_API_URL}/contract/${contractId}/service/${serviceId}`,
		);
		const data = await res.json();

		if (data.success) {
			return data.data as ContractService;
		} else {
			throw new Error(data.message);
		}
	} catch (error: any) {
		console.error(error);
		throw new Error(error ?? "Алдаа гарлаа");
	}
};

export const fetchContractProduct = async (contractProductId: string) => {
	try {
		const res = await fetch(
			`${process.env.REACT_APP_API_URL}/contract/product/${contractProductId}`,
		);
		const data = await res.json();

		if (data.success) {
			return data.data as ContractProduct;
		} else {
			throw new Error(data.message);
		}
	} catch (error: any) {
		console.error(error);
		throw new Error(error ?? "Алдаа гарлаа");
	}
};

export const fetchContractProducts = async (params: {
	customerId?: string;
	contractId?: string;
	page?: number;
	limit?: number;
	search?: string;
}) => {
	try {
		if (!params.customerId && !params.contractId) {
			throw new Error("Хэрэглэгч эсвэл гэрээний дугаар шаардлагатай");
		}

		let url = new URL(
			`${process.env.REACT_APP_API_URL}/contract/${params.contractId ?? params.customerId}/products`,
		);
		if (params.customerId) {
			// add customer id to the query
			url.searchParams.set("customerId", params.customerId);
		}

		if (params.page) {
			url.searchParams.set("page", params.page.toString());
		}

		if (params.limit) {
			url.searchParams.set("limit", params.limit.toString());
		}

		if (params.search) {
			url.searchParams.set("search", params.search);
		}

		const res = await fetch(url);

		const data = await res.json();

		if (data.success) {
			return {
				products: data.data.products as ContractProduct[],
				total: data.data.total,
			};
		} else {
			throw new Error(data.message);
		}
	} catch (error: any) {
		console.error(error);
		throw new Error(error ?? "Алдаа гарлаа");
	}
};

export const fetchContractServices = async (contractId: string) => {
	try {
		const res = await fetch(
			`${process.env.REACT_APP_API_URL}/contract/${contractId}/services`,
		);
		const data = await res.json();

		if (data.success) {
			return data.data as ContractService[];
		} else {
			throw new Error(data.message);
		}
	} catch (error: any) {
		console.error(error);
		throw new Error(error ?? "Алдаа гарлаа");
	}
};

export const fetchContractPackageLogs = async (
	contractId: string,
	packageId: string,
) => {
	try {
		const res = await fetch(
			`${process.env.REACT_APP_API_URL}/contract/${contractId}/package/${packageId}/logs`,
		);
		const data = await res.json();

		if (data.success) {
			return {
				logs: data.data.logs as ContractPackageLog[],
				count: data.data.count,
			};
		} else {
			throw new Error(data.message);
		}
	} catch (error) {
		console.error(error);
		throw new Error("Алдаа гарлаа");
	}
};

export const fetchContractPaymentDetails = async (id: string): Promise<any> => {
	try {
		const res = await fetch(
			`${process.env.REACT_APP_API_URL}/contract/${id}/payment`,
		);
		const data = await res.json();

		if (data.success) {
			return data.data;
		} else {
			throw new Error(data.message);
		}
	} catch (error) {
		console.error(error);
		throw new Error("Алдаа гарлаа");
	}
};

export const fetchContractDevice = async (
	id: string,
	contractId: string,
): Promise<Setbox> => {
	try {
		const res = await fetch(
			`${process.env.REACT_APP_API_URL}/contract/${contractId}/device/${id}`,
		);
		const data = await res.json();

		if (data.success) {
			return data.data as Setbox;
		} else {
			throw new Error(data.message);
		}
	} catch (error: any) {
		console.error(error);
		throw new Error(error ?? "Алдаа гарлаа");
	}
};

export const fetchContractLTE = async (id: string): Promise<SimCard[]> => {
	try {
		const res = await fetch(
			`${process.env.REACT_APP_API_URL}/customer/contract/${id}/lte-sims`,
		);
		const data = await res.json();

		if (data.success) {
			return data.data as SimCard[];
		} else {
			throw new Error(data.message);
		}
	} catch (error) {
		console.error(error);
		throw new Error("Алдаа гарлаа");
	}
};

export const fetchContractDevices = async (contractId: string) => {
	try {
		const res = await fetch(
			`${process.env.REACT_APP_API_URL}/contract/${contractId}/devices`,
		);
		const data = await res.json();

		if (data.success) {
			return data.data as Setbox[];
		} else {
			throw new Error(data.message);
		}
	} catch (error) {
		console.error(error);
		throw new Error("Алдаа гарлаа");
	}
};

export const fetchContractTemplate = async (id: string): Promise<Template> => {
	try {
		const res = await fetch(
			`${process.env.REACT_APP_API_URL}/contract/template/${id}`,
		);
		const data = await res.json();

		if (data.success) {
			return data.data as Template;
		} else {
			throw new Error(data.message);
		}
	} catch (err) {
		console.error(err);
		throw new Error("Алдаа гарлаа");
	}
};

export const fetchContractTemplates = async (): Promise<Template[]> => {
	try {
		const res = await fetch(
			`${process.env.REACT_APP_API_URL}/contract/templates`,
		);
		const data = await res.json();

		if (data.success) {
			return data.data as Template[];
		} else {
			throw new Error(data.message);
		}
	} catch (err) {
		console.error(err);
		throw new Error("Алдаа гарлаа");
	}
};
interface FetchContractsParams {
	customer?: string;
	page?: number;
	limit?: number;
	template?: string;
	status?: string;
	startDate?: string;
	endDate?: string;
	search?: string;
}
export const fetchContracts = async (
	params: FetchContractsParams,
): Promise<{
	contracts: Contract[];
	total: number;
}> => {
	try {
		const res = await fetch(
			`${process.env.REACT_APP_API_URL}/contract?${new URLSearchParams(params as any)}`,
		);
		const data = await res.json();

		if (data.success) {
			return {
				contracts: data.data.contracts,
				total: data.data.total,
			};
		} else {
			throw new Error(data.message);
		}
	} catch (err) {
		console.error(err);
		throw new Error("Алдаа гарлаа");
	}
};

export const fetchContract = async (
	id: string,
	params?: {
		packageStatus?: "active" | "inactive" | "all";
	},
): Promise<Contract> => {
	try {
		const url = new URL(`${process.env.REACT_APP_API_URL}/contract/${id}`);
		if (params?.packageStatus) {
			url.searchParams.set("packageStatus", params.packageStatus);
		}

		const res = await fetch(url.toString());
		const data = await res.json();

		if (data.success) {
			return data.data as Contract;
		} else {
			throw new Error(data.message);
		}
	} catch (err) {
		console.error(err);
		throw new Error("Алдаа гарлаа");
	}
};

export const fetchAdvancedTemplates = async (): Promise<Template[]> => {
	try {
		const res = await fetch(
			`${process.env.REACT_APP_API_URL}/contract/template/list`,
		);
		const data = await res.json();

		if (data.success) {
			return data.data as Template[];
		} else {
			throw new Error(data.message);
		}
	} catch (err) {
		throw err;
	}
};
